import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { extractCredential, upsertCredential } from "../../api/complianceApi";
import "./license-intake.css";

/* Document-first credential intake: upload the license PDF → lane D stores it
   and extracts number / dates / issuing body / holder / category → the admin
   confirms what it read → POST /api/compliance/credentials/.

   Rules from the founder's test (2026-09-08):
   - Nothing is defaulted. A category is shown only if the document yielded one;
     otherwise the field says so and the admin picks. Categories are the
     jurisdiction's real list (the pack) and a license can carry several.
   - Errors are specific: a field the backend rejected is named; "not deployed"
     is said as such; a server failure says the credential wasn't saved. */

const fmtDate = (v) => (v ? new Date(v + (String(v).length === 10 ? "T00:00:00" : "")).toLocaleDateString() : "—");
const norm = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/* Map whatever the extractor returned onto the pack's list (case/punctuation-insensitive);
   unknown values are kept verbatim and flagged so nothing silently disappears. */
function matchCategories(extracted, list) {
  const raw = Array.isArray(extracted) ? extracted : extracted ? String(extracted).split(/[;,/|]|\band\b/) : [];
  const out = [];
  raw.map((x) => x.trim()).filter(Boolean).forEach((x) => {
    const hit = (list || []).find((c) => norm(c) === norm(x) || norm(c).includes(norm(x)) || norm(x).includes(norm(c).replace(" wdo", "")));
    out.push({ value: hit || x, inPack: !!hit });
  });
  return out;
}

function explainError(ex) {
  if (ex?.status === 404) return "Document intake isn’t deployed on the backend yet.";
  if (ex?.status === 403) return "Only admins and managers can add credentials.";
  if (ex?.status >= 500) return "The server failed while saving — the credential was NOT saved. (Server error " + ex.status + ")";
  const p = ex?.payload;
  if (p && typeof p === "object") {
    if (p.detail || p.error) return String(p.detail || p.error);
    const fields = Object.entries(p).filter(([, v]) => Array.isArray(v) || typeof v === "string");
    if (fields.length) return fields.map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(" ") : v}`).join(" · ");
  }
  return ex?.message || "Couldn’t save the credential.";
}

export default function LicenseIntake({ req, tech, existing, onSaved, onCancel, typedFallback = null }) {
  const [stage, setStage] = useState("upload"); // upload | reading | confirm | typed
  const [proposal, setProposal] = useState(null);
  const [holderName, setHolderName] = useState(null);
  const [document, setDocument] = useState(null);
  const [note, setNote] = useState(null);
  const [edited, setEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);
  const isEmployee = req?.holder_level === "employee";
  const schema = req?.attributes_schema || {};
  const packCategories = Array.isArray(schema.category) ? schema.category : [];

  const onFile = async (file) => {
    if (!file) return;
    if (!/pdf$/i.test(file.type) && !/\.pdf$/i.test(file.name)) { setErr("Upload the license as a PDF."); return; }
    setErr(null); setStage("reading");
    try {
      const { data } = await extractCredential(file, { type_key: req.type_key, holder_tech_id: isEmployee ? tech?.tech_id : undefined });
      const p = data?.proposal || {};
      const extractedCats = p.attributes?.categories || p.attributes?.category || null;
      const matched = matchCategories(extractedCats, packCategories);
      setProposal({
        number: p.number || "", issue_date: p.issue_date || "", expiry_date: p.expiry_date || "",
        issuing_body: p.issuing_body || req.issuing_body || "",
        categories: matched.map((m) => m.value), categoriesFromDoc: matched.length > 0, unknownCats: matched.filter((m) => !m.inPack).map((m) => m.value),
        rup: !!p.attributes?.rup, document_url: p.document_url || data?.document?.ref || "",
      });
      setHolderName(data?.extracted_holder_name || null);
      setDocument(data?.document || null);
      setNote(data?.note || null);
      setEdited(false);
      setStage("confirm");
    } catch (ex) {
      if (ex?.status === 404) { setNote("Document intake isn’t deployed on the backend yet — type the credential for now; upload will light up on the next backend deploy."); setStage("typed"); }
      else { setErr(explainError(ex)); setStage("upload"); }
    }
  };

  const set = (k, v) => { setProposal((p) => ({ ...p, [k]: v })); setEdited(true); };
  const toggleCat = (c) => { setProposal((p) => ({ ...p, categories: p.categories.includes(c) ? p.categories.filter((x) => x !== c) : [...p.categories, c] })); setEdited(true); };
  const holderMismatch = !!(holderName && tech?.name && norm(holderName) !== norm(tech.name));

  const confirm = async (e) => {
    e.preventDefault();
    if (!proposal?.number?.trim()) { setErr("The document didn’t yield a number — enter it before confirming."); return; }
    if (packCategories.length && proposal.categories.length === 0) { setErr("Pick at least one category — the document didn’t state one."); return; }
    setBusy(true); setErr(null);
    try {
      const attributes = {};
      if (packCategories.length) { attributes.categories = proposal.categories; attributes.category = proposal.categories[0] || ""; attributes.categories_from_document = !!proposal.categoriesFromDoc && !edited; }
      if (schema.rup) attributes.rup = !!proposal.rup;
      attributes.intake = "document";
      attributes.confirmed_edited = edited;
      const body = {
        type_key: req.type_key, number: proposal.number.trim(),
        issue_date: proposal.issue_date || undefined, expiry_date: proposal.expiry_date || undefined,
        issuing_body: proposal.issuing_body || undefined, attributes,
        document_url: proposal.document_url || undefined,
        // 'confirmed' is the only provenance value that fits the model column (max 16 chars);
        // 'inferred_confirmed' / 'owner_confirmed_edited' overflowed it and the save failed.
        source: "confirmed",
      };
      if (isEmployee && tech?.tech_id != null) body.holder_tech_id = tech.tech_id;
      // A document re-confirm replaces number/dates, not earned CEUs: the
      // upsert defaults a missing ceu_earned to 0, which wiped training credit.
      if (existing?.ceu_earned != null) body.ceu_earned = existing.ceu_earned;
      await upsertCredential(body);
      toast.success(existing ? "Updated from the document" : "Credential confirmed");
      onSaved?.();
    } catch (ex) { setErr(explainError(ex)); }
    finally { setBusy(false); }
  };

  if (stage === "typed" && typedFallback) {
    return <div className="lic">{note && <div className="lic-note warn">{note}</div>}{typedFallback}</div>;
  }

  return (
    <div className="lic">
      {isEmployee && (
        <div className="lic-holder"><span className="lic-label">Holder</span><b>{tech?.name || "—"}</b>{existing ? <span className="lic-dim"> · replacing #{existing.number}</span> : null}</div>
      )}

      {stage === "upload" && (
        <div className="lic-drop" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}>
          <div className="lic-drop-title">Upload the {req.label || "license"} PDF</div>
          <div className="lic-dim">We read the number, dates, issuing body, holder and categories from the document and store the PDF. You confirm what it read — nothing is saved until you do.</div>
          <div className="lic-drop-actions">
            <button type="button" className="lic-btn primary" onClick={() => fileRef.current?.click()}>Choose PDF</button>
            {typedFallback && <button type="button" className="lic-btn" onClick={() => setStage("typed")}>Type it instead</button>}
            {onCancel && <button type="button" className="lic-btn" onClick={onCancel}>Cancel</button>}
          </div>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" hidden onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
          {err && <div className="lic-note danger">{err}</div>}
        </div>
      )}

      {stage === "reading" && <div className="lic-reading">Reading the document…</div>}

      {stage === "confirm" && proposal && (
        <form className="lic-confirm" onSubmit={confirm}>
          <div className="lic-confirm-head">
            <span className="lic-label">What we read</span>
            {document?.ref && <a className="lic-link" href={document.ref} target="_blank" rel="noreferrer">Open the PDF</a>}
          </div>
          {note && <div className="lic-note warn">{note}</div>}
          {holderName && (
            <div className={`lic-note ${holderMismatch ? "warn" : "ok"}`}>
              Document is issued to <b>{holderName}</b>{isEmployee ? (holderMismatch ? ` — that doesn’t match ${tech?.name}. Confirm only if it’s the same person.` : ` — matches ${tech?.name}.`) : "."}
            </div>
          )}
          <div className="lic-grid">
            <label className="lic-field"><span>Number</span><input type="text" value={proposal.number} onChange={(e) => set("number", e.target.value)} autoFocus /></label>
            <label className="lic-field"><span>Expiry</span><input type="date" value={proposal.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} /></label>
            <label className="lic-field"><span>Issued</span><input type="date" value={proposal.issue_date} onChange={(e) => set("issue_date", e.target.value)} /></label>
            <label className="lic-field"><span>Issuing body</span><input type="text" value={proposal.issuing_body} onChange={(e) => set("issuing_body", e.target.value)} /></label>
            {schema.rup && <label className="lic-field lic-field--check"><input type="checkbox" checked={proposal.rup} onChange={(e) => set("rup", e.target.checked)} /><span>RUP endorsement</span></label>}
          </div>
          {packCategories.length > 0 && (
            <div className="lic-cats">
              <span className="lic-label">Categories <span className="lic-dim">{proposal.categoriesFromDoc ? "· read from the document" : "· not stated on the document — pick what applies"}</span></span>
              <div className="lic-cat-grid">
                {[...packCategories, ...proposal.unknownCats.filter((u) => !packCategories.includes(u))].map((c) => (
                  <label key={c} className={`lic-cat ${proposal.categories.includes(c) ? "is-on" : ""} ${proposal.unknownCats.includes(c) ? "is-unknown" : ""}`}>
                    <input type="checkbox" checked={proposal.categories.includes(c)} onChange={() => toggleCat(c)} />
                    <span>{c}</span>{proposal.unknownCats.includes(c) && <em>not in the {req.issuing_body || "pack"} list — verify</em>}
                  </label>
                ))}
              </div>
            </div>
          )}
          {proposal.expiry_date && <div className="lic-dim">Expires {fmtDate(proposal.expiry_date)}{edited ? " · you edited the read-back" : " · as read from the document"}</div>}
          {err && <div className="lic-note danger">{err}</div>}
          <div className="lic-actions">
            <button type="submit" className="lic-btn primary" disabled={busy}>{busy ? "Saving…" : "Confirm credential"}</button>
            <button type="button" className="lic-btn" onClick={() => { setStage("upload"); setProposal(null); }} disabled={busy}>Different PDF</button>
            {onCancel && <button type="button" className="lic-btn" onClick={onCancel} disabled={busy}>Cancel</button>}
          </div>
        </form>
      )}
    </div>
  );
}
